import React, { useMemo, useRef, useState } from "react";
import FormHelperText from "@mui/material/FormHelperText";
import { FileUpload } from "@components/FileUpload/FileUpload";
import SingleFile from "@components/FileUpload/SingleFile";
import { useNotification } from "@context/NotificationContext";
import { useUploadDocument } from "@features/vessel-nomination/add/hooks/useUploadDocument";
import type { DryBulkDecisionAttachment } from "@services/dry-bulk-nomination/types";
import { useTranslation } from "react-i18next";
import {
  ATTACHMENT_EXTENSIONS,
  ATTACHMENT_FORMATS,
  ATTACHMENT_MAX_BYTES,
  DECISION_MAX_ATTACHMENTS,
} from "../constants";

const LANG_BASE = "vessel-nomination.dry-bulk.review";

interface Props {
  testId: string;
  attachments: DryBulkDecisionAttachment[];
  onChange: (attachments: DryBulkDecisionAttachment[]) => void;
  error?: string;
}

/** VD-44: up to 10 files (jpg / png / pdf, 10 MB each), uploaded straight to Azure Blob (SAS) like the draft attachments. */
const DecisionAttachmentsField: React.FC<Props> = ({ testId, attachments, onChange, error }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const [limitError, setLimitError] = useState("");
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  const pending = useRef(new Map<string, { size: number; type: string }>());

  const upload = useUploadDocument({
    multiple: true,
    transaction: {
      module: "dry_bulk_vessel_nomination",
      screen: "DryBulkNominationDetail",
      action: "upload_dry_bulk_decision_attachment",
      entityType: "dry_bulk_nomination_document",
    },
    handleUploadSuccess: ({ documents }) => {
      const added = documents.map(({ documentUrl, documentName }) => ({
        fileName: documentName,
        fileUrl: documentUrl,
        fileSize: pending.current.get(documentName)?.size ?? 0,
        contentType: pending.current.get(documentName)?.type || undefined,
      }));
      onChange([...attachmentsRef.current, ...added]);
    },
    handleUploadError: () =>
      notify({
        type: "error",
        title: t(`${LANG_BASE}.upload-error-title`),
        description: t(`${LANG_BASE}.upload-error-description`),
      }),
  });

  const conditions = useMemo(
    () => ({
      format: ATTACHMENT_FORMATS,
      maxSizeLabel: t("upload.max-size", { size: "10MB" }),
      maxSizeBytes: ATTACHMENT_MAX_BYTES,
      accept: {},
    }),
    [t],
  );

  const entries = attachments.map(({ fileName, fileUrl }) => ({ name: fileName, url: fileUrl }));
  const isFull = attachments.length >= DECISION_MAX_ATTACHMENTS;

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <span className="text-sm font-medium" data-testid={`${testId}-label`}>
        {t(`${LANG_BASE}.attachments`)}
      </span>
      <FileUpload
        testId={`${testId}-upload`}
        name={testId}
        conditions={conditions}
        files={entries}
        setFiles={() => undefined}
        maxFiles={DECISION_MAX_ATTACHMENTS}
        allowedExtensions={ATTACHMENT_EXTENSIONS}
        disabled={upload.isPending || isFull}
        isSideDrawer
        uploadDescription={t("upload.additional-documents-upload", {
          extensions: ATTACHMENT_FORMATS.join(", "),
          size: "10MB",
        })}
        validateFiles={(incoming) =>
          attachments.length + incoming.length > DECISION_MAX_ATTACHMENTS
            ? t("form-validation.max-files", { max: DECISION_MAX_ATTACHMENTS })
            : null
        }
        onValidationResult={(message) => setLimitError(message ?? "")}
        onUpload={(files) => {
          files.forEach((file) =>
            pending.current.set(file.name, { size: file.size, type: file.type }),
          );
          upload.mutate({ files, documentType: "OTHERS", startIdx: attachments.length });
        }}
      />
      {(limitError || error) && (
        <FormHelperText error data-testid={`${testId}-error`}>
          {limitError || error}
        </FormHelperText>
      )}
      {attachments.map((attachment, index) => (
        <SingleFile
          key={attachment.fileUrl}
          testId={`${testId}-file-${index}`}
          file={{ name: attachment.fileName, url: attachment.fileUrl }}
          fieldName={testId}
          currentFileIndex={index}
          setFiles={() => onChange(attachments.filter((_, i) => i !== index))}
        />
      ))}
    </div>
  );
};

export default DecisionAttachmentsField;
